import React from 'react';
import { Box } from '@components/Box';
import { Flex } from '@components/Flex';
import { addRendition } from '@utils/photoRendition';
import { EXT_APP_ROOT_URL } from '@utils/urlHelpers.util';
import { EventType } from '@sections/find/HeroSection/HeroSection.types';
import { getEvenType } from '@sections/find/HeroSection/HeroSection.utils';
import { PlaceholderAvatar } from '@sections/find/HeroSection/components/PlaceholderAvatars/PlaceholderAvatar';

import {
  AvatarImageContainer,
  DateField,
  DetailField,
  DetailFieldMobile,
  ListItemWrapper,
  NameField,
  StyledCoupleAvatar,
  LinksWrapper,
  EventTypeField,
  StyledCTALink,
  DateFieldMobile,
} from './ResultListItem.styles';
import { Integrations } from '@static/js/types';
import { useTranslation } from 'react-i18next';

const analytics = require('/static/js/joy');

type ListItemProps = {
  ownerFullName: string;
  fianceeFullName?: string;
  eventDate: string;
  website: string;
  photoUrl: string | null;
  eventType: EventType;
  index: number;
  registryPageDisabled: boolean;
  showBorderBottoom: boolean;
};

interface ItemDetailsProps {
  id: string;
  ownerFullName: ListItemProps['ownerFullName'];
  eventDate: ListItemProps['eventDate'];
  website: ListItemProps['website'];
  registryPageDisabled: ListItemProps['registryPageDisabled'];
  websiteClicked: (e: React.MouseEvent) => void;
}

const buildWebsiteUrlWithUTM = (website: Readonly<string>, path: Readonly<string> = '') => {
  try {
    const url = new URL(`${EXT_APP_ROOT_URL}/${website}${path}`);

    url.searchParams.set('utm_campaign', 'find-a-couple');
    url.searchParams.set('utm_medium', 'web');
    url.searchParams.set('utm_source', 'marcom');

    return url.toString();
  } catch {
    return `${EXT_APP_ROOT_URL}/${website}${path}`;
  }
};

const ItemDetails: React.FC<ItemDetailsProps> = ({ id, ownerFullName, eventDate, website, registryPageDisabled, websiteClicked }) => {
  const registryClicked = (e: React.MouseEvent) => {
    e.preventDefault();
    analytics.segment.sendTelemetryEvent('newmarketing.findACoupleRegistryClicked', { website }, undefined, {
      integrations: { [Integrations.Amplitude]: true },
    });
    window.open(buildWebsiteUrlWithUTM(website, '/registry'), '_blank');
  };
  const { t } = useTranslation('findAnEvent');
  const heroSectionData = t('findACouple.heroSection');

  return (
    <>
      <DateFieldMobile tagName="span" variant="dek15" color="mono1">
        {eventDate}
      </DateFieldMobile>
      <LinksWrapper>
        {website && (
          <>
            <StyledCTALink
              href={buildWebsiteUrlWithUTM(website)}
              id={`${ownerFullName}-website-${id}`}
              target="_blank"
              typography={'featureHed17'}
              hasChevron={true}
              onClick={websiteClicked}
            >
              {heroSectionData.website}
            </StyledCTALink>
            {!registryPageDisabled ? (
              <Box marginLeft={[5, 0]}>
                <StyledCTALink
                  href={buildWebsiteUrlWithUTM(website, '/registry')}
                  id={`${ownerFullName}-registry-${id}`}
                  onClick={registryClicked}
                  target="_blank"
                  typography={'featureHed17'}
                  hasChevron={true}
                >
                  {heroSectionData.registry}
                </StyledCTALink>
              </Box>
            ) : null}
          </>
        )}
      </LinksWrapper>
    </>
  );
};

export const ResultListItem: React.FC<ListItemProps> = (props) => {
  const { ownerFullName, fianceeFullName, eventDate, website, photoUrl, eventType, index, registryPageDisabled, showBorderBottoom } = props;

  const websiteClicked = (e: React.MouseEvent) => {
    e.preventDefault();
    analytics.segment.sendTelemetryEvent('newmarketing.findACoupleWebsiteClicked', { website }, undefined, {
      integrations: { [Integrations.Amplitude]: true },
    });
    window.open(buildWebsiteUrlWithUTM(website), '_blank');
  };

  return (
    <ListItemWrapper paddingY={6} paddingX={[5, 0]} showBorderBottoom={showBorderBottoom}>
      <Flex alignItems="center" css={{ gap: 24 }} paddingY={4}>
        {photoUrl ? (
          <a
            target="_blank"
            rel="noopener noreferrer"
            id={`${ownerFullName}-website`}
            onClick={websiteClicked}
            href={buildWebsiteUrlWithUTM(website)}
          >
            <AvatarImageContainer>
              <StyledCoupleAvatar src={addRendition({ url: photoUrl, renditionSize: 'avatar' })} alt={'couple avatar'} />
            </AvatarImageContainer>
          </a>
        ) : (
          <a target="_blank" rel="noopener noreferrer" id={`${ownerFullName}-website`} href={buildWebsiteUrlWithUTM(website)}>
            <AvatarImageContainer>
              <PlaceholderAvatar avatarIndex={index % 6} />
            </AvatarImageContainer>
          </a>
        )}

        <Flex onClick={websiteClicked} alignItems="flex-start" css={{ gap: 4 }} flexDirection="column">
          <EventTypeField variant="austinNewsDek24" tagName="span" color="mono1">
            {getEvenType(eventType)}
          </EventTypeField>
          <NameField tagName="span" variant="austinNewsDek24" color="mono1">
            {`${ownerFullName}`}
            {fianceeFullName ? (
              <span>
                {' '}
                & <span className="fiancee">{`${fianceeFullName}`}</span>
              </span>
            ) : null}
          </NameField>
          <DetailFieldMobile>
            <ItemDetails
              id="mobile"
              ownerFullName={ownerFullName}
              eventDate={eventDate}
              website={website}
              registryPageDisabled={registryPageDisabled}
              websiteClicked={websiteClicked}
            />
          </DetailFieldMobile>
        </Flex>
      </Flex>
      <DateField tagName="span" variant="dek15" color="mono1">
        {eventDate}
      </DateField>
      <DetailField>
        <ItemDetails
          id="desktop"
          ownerFullName={ownerFullName}
          eventDate={eventDate}
          website={website}
          registryPageDisabled={registryPageDisabled}
          websiteClicked={websiteClicked}
        />
      </DetailField>
    </ListItemWrapper>
  );
};
